import _ from "lodash";
import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useReducer } from "react";

import type { TrialMetadataInfo } from "../../../../apis/types/trialTypes";

/**
 * Discriminates what kind of thing the "Assessment" dropdown is pointing at.
 *
 * Deliberately NOT called `assessmentType`: ProtocolSummaryTableEditor already uses that name for
 * a display category label ("Phenotype", "Growth Parameter", "Ground Data", "Timeseries").
 */
export type AssessmentKind = "analysis" | "grounddata" | "curvemodel" | "standcount";

// Shared by every row in the protocol summary table's assessment list.
type BaseAssessmentSelection = {
	// curveModelName | groundDataAssessmentId | analysisId (stand count)
	assessmentId: string;
	assessmentName: string;
	dataNormalizationId: string | null;
	dataNormalizationName: string | null;
	unitName: string | null;
	checked: boolean;
	visible: boolean;
};

export type CurveModelAssessmentSelection = BaseAssessmentSelection & {
	kind: "curvemodel";
	curveModelId: string | null;
	curveModelAnalysisId: string;
	analysisId: string;
	analysisTypeId: string;
	// curve models have no growth phase; `plannedTimingName` is the curve model timing name
	plannedTimingId: null;
	plannedTimingName: string | null;
};

export type StandCountAssessmentSelection = BaseAssessmentSelection & {
	kind: "standcount";
	analysisId: string;
	analysisTypeId: string;
	curveModelAnalysisId: null;
	plannedTimingId: null;
	plannedTimingName: null;
};

export type GroundDataAssessmentSelection = BaseAssessmentSelection & {
	kind: "grounddata";
	// groundDataGrowthPhaseId / groundDataGrowthPhaseName
	plannedTimingId: string;
	plannedTimingName: string;
	analysisId: null;
	analysisTypeId: null;
	curveModelAnalysisId: null;
};

export type ProtocolAssessmentSelection =
	| CurveModelAssessmentSelection
	| StandCountAssessmentSelection
	| GroundDataAssessmentSelection;

export interface AnalysisSettings {
	// options
	removeDamagedOrExcluded: boolean;
	removeOutliers: boolean;
	outlierType: number;
	isPlotUniformity: boolean;

	// treatment/trial inclusion — both are sent to the protocol mean comparison endpoint as
	// unchecked lists, so a checkbox change is a pending change like any other setting
	selectedTreatmentMatrix: boolean[] | null; // ordered by trialTreatmentId
	selectedTrialIds: string[] | null;

	// assessment selection (box whisker, piano, summary table)
	assessmentValue: string | null; // the "Assessment" dropdown key
	assessmentKind: AssessmentKind | null;
	// The timecourse selects a time series preset instead. Kept separate from assessmentValue
	// because the two are different id spaces, and the provider spans all four tabs — sharing one
	// field would leave a preset id selected after switching to the box whisker.
	timecoursePresetId: string | null;
	// The timecourse's own analysis. analysisId and analysisTypeId are what the current tab shows, and
	// the other tabs overwrite them with their assessment's ids, so the timecourse restores these.
	timecourseAnalysisId: string | null;
	timecourseAnalysisTypeId: string | null;
	analysisId: string | null;
	analysisTypeId: string | null;
	quantifiedRegionTypeId: string | null;
	curveModelAnalysisId: string | null;
	groundDataAssessmentId: string | null;
	plannedTimingId: string | null; // ground data growth phase

	// statistics
	meanComparisonOptionId: string | null;
	alphaOptionId: string | null;
	dataNormalizationId: string | null;

	// timecourse
	useGdu: boolean; // false = DAP axis, true = GDU axis
	timecourseStart: number;
	timecourseEnd: number;

	// summary table
	summaryTablePresetId: string | null;
	configuredAssessments: ProtocolAssessmentSelection[];
}

export interface AnalysisDisplaySettings {
	// names/labels derived from the current selection
	assessmentName: string | null;
	analysisName: string | null;
	treatmentNames: string[];

	// every trial in the protocol, used to resolve which selected trials actually have data
	trialIds: string[];

	// availability of the GDU timecourse axis
	allTrialsHavePlantDate: boolean;
	hasGduFormula: boolean;

	// view toggles
	anovaType: "one-way" | "two-way";
	showAverages: boolean;
	disableColors: boolean;
	hideUncheckedTrialData: boolean;
	hideUncheckedTreatmentData: boolean;
	hideLineCharts: boolean;
	collapseView: boolean;

	// selected saved trial filter ("custom" once the user hand-picks trials)
	trialFilterId: string | null;
	trialFilters: {
		quality: string[];
		status: string[];
		response: string[];
	};

	// the meta tags table locks committing while a row is being edited
	isEditingMetaTags: boolean;
}

/** The meta tag ids a user can change on a trial. */
export type MetaTagEdit = Partial<
	Pick<TrialMetadataInfo, "trialExclusionStatusId" | "trialDataQualityId" | "trialSiteResponseId">
>;

/** Unsaved meta tag changes, keyed by trialId. Holds only the fields that differ from the saved value. */
export type MetaTagEdits = Record<string, MetaTagEdit>;

type AnalysisAction =
	| {
			type: "change" | "initialize";
			value: Partial<AnalysisSettings>;
			silent?: boolean;
	  }
	| {
			type: "commit" | "reset";
	  }
	| {
			type: "change display";
			value: Partial<AnalysisDisplaySettings>;
	  }
	| {
			type: "edit meta tag";
			trialId: string;
			value: MetaTagEdit;
	  }
	| {
			type: "clear meta tag edits";
	  };

interface AnalysisState {
	committed: AnalysisSettings;
	pending: AnalysisSettings;
	display: AnalysisDisplaySettings;
	isDirty: boolean;
	// unsaved meta tag changes. Separate from isDirty, which only tracks pending vs committed settings
	metaTagEdits: MetaTagEdits;
	isInitialized: boolean;
	isAucOutdated: boolean;
	isLettersOutdated: boolean;
	isNormalizationOutdated: boolean;
	actualSelectedTrials: string[];
	hasUnsavedMetaTags: boolean;
}

interface AnalysisFunctions {
	changeSettings: (settings: Partial<AnalysisSettings>) => void;
	changeSettingsSilently: (settings: Partial<AnalysisSettings>) => void;
	changeDisplaySettings: (settings: Partial<AnalysisDisplaySettings>) => void;
	/** Merges into the trial's edit. A field set to undefined is dropped, i.e. reverted to the saved value. */
	editMetaTag: (trialId: string, value: MetaTagEdit) => void;
	clearMetaTagEdits: () => void;
	initializeSettings: (settings: Partial<AnalysisSettings>) => void;
	commitSettings: () => void;
	resetSettings: () => void;
}

// reserved for settings that involve a call to the database
const initialSettings: AnalysisSettings = {
	removeDamagedOrExcluded: true,
	removeOutliers: false,
	outlierType: 3,
	isPlotUniformity: false,

	selectedTreatmentMatrix: null,
	selectedTrialIds: null,

	assessmentValue: null,
	assessmentKind: null,
	timecoursePresetId: null,
	timecourseAnalysisId: null,
	timecourseAnalysisTypeId: null,
	analysisId: null,
	analysisTypeId: null,
	quantifiedRegionTypeId: null,
	curveModelAnalysisId: null,
	groundDataAssessmentId: null,
	plannedTimingId: null,

	meanComparisonOptionId: null,
	alphaOptionId: null,
	dataNormalizationId: null,

	useGdu: false,
	timecourseStart: 0,
	timecourseEnd: 0,

	summaryTablePresetId: "none",
	configuredAssessments: [],
};

// reserved for settings that do not involve a call to the database
const initialDisplaySettings: AnalysisDisplaySettings = {
	assessmentName: null,
	analysisName: null,
	treatmentNames: [],

	trialIds: [],

	allTrialsHavePlantDate: false,
	hasGduFormula: false,

	anovaType: "one-way",
	showAverages: true,
	disableColors: false,
	hideUncheckedTrialData: false,
	hideUncheckedTreatmentData: false,
	hideLineCharts: false,
	collapseView: false,

	trialFilterId: null,
	trialFilters: {
		quality: [],
		status: [],
		response: [],
	},

	isEditingMetaTags: false,
};

const initialState: AnalysisState = {
	committed: initialSettings,
	pending: initialSettings,
	display: initialDisplaySettings,
	isDirty: false,
	metaTagEdits: {},
	isInitialized: false,
	isAucOutdated: false,
	isLettersOutdated: false,
	isNormalizationOutdated: false,
	actualSelectedTrials: [],
	hasUnsavedMetaTags: false,
};

const protocolAnalysisReducer = (state: AnalysisState, action: AnalysisAction) => {
	switch (action.type) {
		case "change":
			return {
				...state,
				pending: { ...state.pending, ...action.value },
				isDirty: state.isDirty || !action.silent,
			};
		case "change display":
			return {
				...state,
				display: { ...state.display, ...action.value },
			};
		case "edit meta tag": {
			const edit = _.omitBy({ ...state.metaTagEdits[action.trialId], ...action.value }, (v) => v === undefined);
			const metaTagEdits = _.isEmpty(edit)
				? _.omit(state.metaTagEdits, action.trialId)
				: { ...state.metaTagEdits, [action.trialId]: edit };
			return { ...state, metaTagEdits };
		}
		case "clear meta tag edits":
			return { ...state, metaTagEdits: {} };
		case "commit":
			return {
				...state,
				committed: state.pending,
				isDirty: false,
			};
		case "initialize":
			return {
				...state,
				pending: { ...state.pending, ...action.value },
				committed: { ...state.committed, ...action.value },
				isInitialized: true,
			};
		case "reset":
			return initialState;
	}
};

/**
 * Converts a treatment matrix to a list of unchecked treatments.
 * A null matrix means every treatment is ticked, so it
 * compares equal to the full matrix the first tick builds.
 */
const getUntickedTreatmentIds = (matrix: boolean[] | null): number[] =>
	matrix ? _.filter(_.range(matrix.length), (id) => matrix[id] === false) : [];

const ProtocolAnalysisContext = createContext<AnalysisState | null>(null);
const ProtocolAnalysisDispatchContext = createContext<AnalysisFunctions | null>(null);

const ProtocolAnalysisProvider = ({ children }: PropsWithChildren) => {
	const [analysis, dispatch] = useReducer(protocolAnalysisReducer, initialState);

	// the AUC is integrated over the timecourse window, so the window and the axis it is
	// measured on invalidate it
	const isAucOutdated = useMemo(() => {
		return (
			analysis.committed.useGdu !== analysis.pending.useGdu ||
			Number(analysis.committed.timecourseStart) !== Number(analysis.pending.timecourseStart) ||
			Number(analysis.committed.timecourseEnd) !== Number(analysis.pending.timecourseEnd)
		);
	}, [
		analysis.committed.useGdu,
		analysis.committed.timecourseStart,
		analysis.committed.timecourseEnd,
		analysis.pending.useGdu,
		analysis.pending.timecourseStart,
		analysis.pending.timecourseEnd,
	]);

	const isLettersOutdated = useMemo(() => {
		const treatmentMismatch = !_.isEqual(
			getUntickedTreatmentIds(analysis.committed.selectedTreatmentMatrix),
			getUntickedTreatmentIds(analysis.pending.selectedTreatmentMatrix),
		);
		const trialMismatch = _.xor(analysis.committed.selectedTrialIds, analysis.pending.selectedTrialIds).length !== 0;
		const meanComparisonOptsMismatch =
			analysis.committed.meanComparisonOptionId !== analysis.pending.meanComparisonOptionId ||
			analysis.committed.alphaOptionId !== analysis.pending.alphaOptionId;

		return treatmentMismatch || trialMismatch || meanComparisonOptsMismatch;
	}, [
		analysis.committed.alphaOptionId,
		analysis.committed.meanComparisonOptionId,
		analysis.committed.selectedTreatmentMatrix,
		analysis.committed.selectedTrialIds,
		analysis.pending.alphaOptionId,
		analysis.pending.meanComparisonOptionId,
		analysis.pending.selectedTreatmentMatrix,
		analysis.pending.selectedTrialIds,
	]);

	const isNormalizationOutdated = useMemo(() => {
		return analysis.committed.dataNormalizationId !== analysis.pending.dataNormalizationId;
	}, [analysis.committed.dataNormalizationId, analysis.pending.dataNormalizationId]);

	const actualSelectedTrials = useMemo(() => {
		return _.intersection(analysis.display.trialIds, analysis.pending.selectedTrialIds);
	}, [analysis.display.trialIds, analysis.pending.selectedTrialIds]);

	const changeSettings = useCallback(
		(settings: Partial<AnalysisSettings>) => dispatch({ type: "change", value: settings, silent: false }),
		[],
	);

	const changeSettingsSilently = useCallback(
		(settings: Partial<AnalysisSettings>) => dispatch({ type: "change", value: settings, silent: true }),
		[],
	);

	const changeDisplaySettings = useCallback(
		(settings: Partial<AnalysisDisplaySettings>) => dispatch({ type: "change display", value: settings }),
		[],
	);

	const editMetaTag = useCallback(
		(trialId: string, value: MetaTagEdit) => dispatch({ type: "edit meta tag", trialId, value }),
		[],
	);

	const clearMetaTagEdits = useCallback(() => dispatch({ type: "clear meta tag edits" }), []);

	const commitSettings = useCallback(() => dispatch({ type: "commit" }), []);

	const resetSettings = useCallback(() => dispatch({ type: "reset" }), []);

	const initializeSettings = useCallback(
		(settings: Partial<AnalysisSettings>) => dispatch({ type: "initialize", value: settings }),
		[],
	);

	return (
		<ProtocolAnalysisContext.Provider
			value={{
				...analysis,
				isAucOutdated,
				isLettersOutdated,
				isNormalizationOutdated,
				actualSelectedTrials,
				hasUnsavedMetaTags: !_.isEmpty(analysis.metaTagEdits),
			}}
		>
			<ProtocolAnalysisDispatchContext.Provider
				value={{
					changeSettings,
					changeSettingsSilently,
					changeDisplaySettings,
					editMetaTag,
					clearMetaTagEdits,
					initializeSettings,
					commitSettings,
					resetSettings,
				}}
			>
				{children}
			</ProtocolAnalysisDispatchContext.Provider>
		</ProtocolAnalysisContext.Provider>
	);
};

const useProtocolAnalysis = () => {
	const context = useContext(ProtocolAnalysisContext);

	if (context === null) {
		throw new Error("ProtocolAnalysisContext is not provided");
	}

	return context;
};

const useProtocolAnalysisDispatch = () => {
	const context = useContext(ProtocolAnalysisDispatchContext);

	if (context === null) {
		throw new Error("ProtocolAnalysisDispatchContext is not provided");
	}

	return context;
};

export { ProtocolAnalysisProvider, useProtocolAnalysis, useProtocolAnalysisDispatch };
